import { useEffect, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { refreshTokenIsValid } from '@/reducers'
import { useAppSelector } from '@/hooks'
import { useWhiteLabelData } from '@/hooks/whitelabel/useWhiteLabelData'
import useRemoteConfig from '@/hooks/useRemoteConfig'
import parseTemplate from '@/utils/string/parseTemplate'
import { getProgramFaqUrl } from '@/utils/utility-program-helpers/getProgramFaqUrl'
import { publicProgramRewardsCollection } from '@/reducers/publicProgramRewards'
import {
  buildRewardsByCategory,
  computeTotalRewardsDollars,
} from '@/utils/program-rewards/rewardCategoryHelpers'
import { searchableUtilityProgramsCollection } from '@/reducers/utilityPrograms'
import { UtilityProgram } from '@/types/utilityProgram'

const DEFAULT_SIGNUP_TITLE_TEMPLATE =
  'Join ${sponsor_name} ${program_name} and earn rewards for charging your EV at home.'

const DEFAULT_SIGNUP_TITLE_GENERIC =
  'Join today and earn rewards for charging your EV at home.'

function getDefaultSignupTitle(
  sponsorName: string,
  programName: string,
): string {
  if (sponsorName || programName) {
    return parseTemplate(DEFAULT_SIGNUP_TITLE_TEMPLATE, {
      sponsor_name: sponsorName,
      program_name: programName,
    })
  }
  return DEFAULT_SIGNUP_TITLE_GENERIC
}

export default function useProgramSignupViewModel(programId: number | null) {
  const history = useHistory()
  const authenticated = useAppSelector((state) => refreshTokenIsValid(state))
  const isGeneric = programId === null

  const {
    whiteLabelData,
    viewConfig,
    isLoading: loadingTheme,
    fetchError,
    hasProgramData,
  } = useWhiteLabelData(isGeneric ? undefined : programId)

  const { isLoading: remoteConfigLoading } = useRemoteConfig()

  // Fetch utility program for faqUrl and total_incentives_usd
  const { data: utilityPrograms } =
    searchableUtilityProgramsCollection.useFetch(programId ?? 0, {
      require: !isGeneric,
    })
  const utilityProgram = utilityPrograms?.[0] as UtilityProgram | undefined

  // Fetch public program rewards
  const { data: rewards, isLoadingOrIdle: rewardsLoading } =
    publicProgramRewardsCollection.useFetch(programId ?? 0, {
      require: !isGeneric,
    })

  // Redirect if program not found — only after loading completes with a definitive answer
  useEffect(
    function redirectIfProgramNotFound() {
      if (isGeneric) return
      if (loadingTheme || remoteConfigLoading) return
      if (hasProgramData === undefined) return
      const programNotFound = Boolean(fetchError) || hasProgramData === false
      if (programNotFound) {
        history.replace('/signup')
      }
    },
    [
      isGeneric,
      loadingTheme,
      remoteConfigLoading,
      fetchError,
      hasProgramData,
      history,
    ],
  )

  const sponsorName = viewConfig?.sponsorName ?? ''
  const programName = viewConfig?.name ?? ''
  const rawSignupText = viewConfig?.signupPage?.signupText

  const resolvedSignupText = useMemo(
    function resolveSignupText() {
      if (isGeneric) return DEFAULT_SIGNUP_TITLE_GENERIC
      const templateVars = {
        utility_name: sponsorName,
        sponsor_name: sponsorName,
        program_name: programName,
      }
      return rawSignupText
        ? parseTemplate(rawSignupText, templateVars)
        : getDefaultSignupTitle(sponsorName, programName)
    },
    [isGeneric, rawSignupText, sponsorName, programName],
  )

  const programLogoUrl = viewConfig?.bannerLogoUrl ?? viewConfig?.logoUrl

  const faqUrl = getProgramFaqUrl(utilityProgram)

  const rewardsByCategory = useMemo(
    function computeRewardsByCategory() {
      if (!rewards || rewards.length === 0) return null
      return buildRewardsByCategory(rewards)
    },
    [rewards],
  )

  const totalRewardsDollars = useMemo(
    function computeTotal() {
      return computeTotalRewardsDollars(rewardsByCategory)
    },
    [rewardsByCategory],
  )

  const isLoading = isGeneric
    ? remoteConfigLoading
    : loadingTheme || remoteConfigLoading

  return {
    authenticated,
    isLoading,
    rewardsLoading,
    resolvedSignupText,
    programLogoUrl,
    faqUrl,
    rewardsByCategory,
    totalRewardsDollars,
    whiteLabelData,
    viewConfig,
    utilityProgram,
    programId,
    isGeneric,
  }
}
